import React, { forwardRef } from "react";
import { Icon } from "@/components/Icon";
import { Tooltip, type TooltipPlacement } from "@/components/Tooltip";

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    text?: string;
    variant?:
        | "basic"
        | "primary"
        | "primaryinverted"
        | "secondary"
        | "success"
        | "successinverted"
        | "danger"
        | "error"
        | "warning"
        | "warninginverted"
        | "outline"
        | "ghost"
        | "link"
        | "emerald"
        | "custom"
        | "black";
    size?: "xs" | "sm" | "md" | "lg" | "xl" | "none";
    icon?: React.ReactNode | string;
    startIcon?: React.ReactNode | string;
    endIcon?: React.ReactNode | string;
    loading?: boolean;
    isLoading?: boolean;
    loadingText?: string;
    fullWidth?: boolean;
    tooltip?: string;
    tooltipPlacement?: TooltipPlacement;
    rounded?: boolean;
    onlyicon?: boolean;
    contentClassName?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            text,
            variant = "primary",
            size = "md",
            icon,
            startIcon,
            endIcon,
            loading = false,
            isLoading = false,
            loadingText,
            disabled = false,
            fullWidth = false,
            type = "button",
            onClick,
            className = "",
            tooltip,
            tooltipPlacement = "top",
            rounded = false,
            onlyicon = false,
            contentClassName = "",
            ...props
        },
        ref
    ) => {
        const isBusy = loading || isLoading;
        const content = text || children;
        const isIconOnly = onlyicon || (!content && Boolean(icon || startIcon));
        const effectiveTooltip =
            tooltip ||
            (isIconOnly && props.title ? String(props.title) : undefined);

        const getIconSize = (): number => {
            switch (size) {
                case "xs":
                    return 14;
                case "sm":
                    return 16;
                case "lg":
                    return 20;
                case "xl":
                    return 24;
                case "none":
                    return 16;
                case "md":
                default:
                    return 18;
            }
        };

        const renderIconNode = (
            ic: React.ReactNode | string
        ): React.ReactNode => {
            if (!ic) {
                return null;
            }
            if (typeof ic === "string") {
                if (onlyicon) {
                    return <Icon value={ic} width="1em" height="1em" />;
                }
                return <Icon value={ic} size={getIconSize()} />;
            }
            if (React.isValidElement(ic)) {
                const element = ic as React.ReactElement<{
                    width?: string | number;
                    height?: string | number;
                    size?: number;
                }>;
                const elementProps = element.props;
                if (onlyicon) {
                    if (
                        !elementProps.width &&
                        !elementProps.height &&
                        !elementProps.size
                    ) {
                        return React.cloneElement(element, {
                            width: "1em",
                            height: "1em",
                        });
                    }
                    return ic;
                }
                if (!elementProps.size && size !== "none") {
                    return React.cloneElement(element, {
                        size: getIconSize(),
                    });
                }
            }
            return ic;
        };

        const getOnlyIconVariantClasses = (): string => {
            switch (variant) {
                case "danger":
                case "error":
                    return "text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-200";
                case "warning":
                case "warninginverted":
                    return "text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-200";
                case "success":
                case "successinverted":
                case "emerald":
                    return "text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200";
                case "primary":
                case "primaryinverted":
                    return "text-primary hover:text-primary-dark";
                case "secondary":
                case "basic":
                case "ghost":
                case "link":
                case "outline":
                    return "text-slate-400 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-200";
                case "custom":
                    return "";
                case "black":
                    return "text-white";
                default:
                    return "text-primary hover:text-primary-dark";
            }
        };

        const getOnlyIconSizeClasses = (): string => {
            switch (size) {
                case "xs":
                    return "p-1 rounded-md text-xs";
                case "sm":
                    return "p-1.5 rounded-lg text-sm";
                case "lg":
                    return "p-2 rounded-xl text-lg";
                case "xl":
                    return "p-2 rounded-xl text-xl";
                case "none":
                    return "p-0";
                case "md":
                default:
                    return "p-1.5 rounded-lg text-base";
            }
        };

        const baseClasses =
            size === "none" && variant === "custom" ? "" : "btn";

        const sizeClasses =
            size === "none"
                ? ""
                : {
                      xs: "btn-xs",
                      sm: "btn-sm",
                      md: "btn-md",
                      lg: "btn-lg",
                      xl: "btn-xl",
                  }[size] || "btn-md";

        const variantClasses =
            variant === "custom"
                ? ""
                : {
                      basic: "btn-secondary",
                      primary: "primary",
                      primaryinverted: "primaryinverted",
                      secondary: "secondary",
                      danger: "danger",
                      outline: "outline",
                      ghost: "ghost !border-transparent !shadow-none dark:text-zinc-300 dark:hover:bg-white/5",
                      link: "!border-transparent !shadow-none bg-transparent text-primary hover:underline p-0 h-auto active:scale-100",
                      emerald:
                          "bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs border border-transparent",
                      error: "danger",
                      warning: "warning",
                      warninginverted: "warninginverted",
                      success: "success",
                      successinverted: "successinverted",
                      black: "bg-black text-white hover:bg-black/80",
                  }[variant] || "primary";

        const leadingIcon = icon || startIcon;

        const buttonProps = { ...props };
        if (effectiveTooltip) {
            delete buttonProps.title;
        }

        const classes = onlyicon
            ? `inline-flex items-center justify-center cursor-pointer select-none transition-colors duration-150 active:scale-90 disabled:opacity-40 disabled:cursor-not-allowed bg-transparent hover:bg-transparent border-transparent shrink-0 ${getOnlyIconSizeClasses()} ${getOnlyIconVariantClasses()} ${rounded ? "!rounded-full" : ""} ${className}`.trim()
            : `
            ${baseClasses} ${sizeClasses} ${variantClasses} ${fullWidth ? "w-full" : ""} ${className}
            ${rounded ? "!rounded-full !w-8 !h-8 !p-0 !min-w-0 !min-h-0 flex items-center justify-center" : ""}
        `.trim();

        const buttonElement = (
            <button
                ref={ref}
                type={type}
                disabled={disabled || isBusy}
                onClick={onClick}
                className={classes}
                {...buttonProps}
                aria-label={
                    props["aria-label"] ||
                    (isIconOnly ? props.title || tooltip : undefined)
                }
            >
                {isBusy ? (
                    <>
                        <Icon
                            value="loader"
                            size={getIconSize()}
                            className="animate-spin shrink-0"
                        />
                        {!onlyicon && loadingText && (
                            <span className="leading-none">{loadingText}</span>
                        )}
                    </>
                ) : onlyicon ? (
                    <span className="shrink-0 inline-flex items-center justify-center">
                        {renderIconNode(leadingIcon || endIcon)}
                    </span>
                ) : (
                    <>
                        {leadingIcon && (
                            <span className="shrink-0 inline-flex items-center justify-center">
                                {renderIconNode(leadingIcon)}
                            </span>
                        )}
                        {content && (
                            <span
                                className={`inline-flex min-w-0 items-center justify-center gap-2 leading-none ${contentClassName}`}
                            >
                                {content}
                            </span>
                        )}
                        {endIcon && (
                            <span className="shrink-0 inline-flex items-center justify-center">
                                {renderIconNode(endIcon)}
                            </span>
                        )}
                    </>
                )}
            </button>
        );

        if (effectiveTooltip) {
            return (
                <Tooltip title={effectiveTooltip} placement={tooltipPlacement}>
                    {disabled ? (
                        <span className="inline-block">{buttonElement}</span>
                    ) : (
                        buttonElement
                    )}
                </Tooltip>
            );
        }

        return buttonElement;
    }
);

Button.displayName = "Button";
export default Button;
